import { ChecklistModuleDefinition } from '../../types/due-diligence';

export const waterModule: ChecklistModuleDefinition = {
  code: 'water',
  name: 'Water Resources',
  description: 'Wells, springs, water quality, and water systems',
  categories: [
    {
      code: 'water_source',
      name: 'Water Source',
      items: [
        {
          code: 'water_source_type',
          title: 'Water source identification',
          description: 'Identify all water sources on property',
          isGroupTask: false,
          dataFields: [
            { key: 'sources', label: 'Water Sources', type: 'multiselect', options: [
              { value: 'municipal', label: 'Municipal/Public Water' },
              { value: 'well', label: 'Private Well' },
              { value: 'spring', label: 'Spring' },
              { value: 'cistern', label: 'Cistern/Collection' },
              { value: 'pond', label: 'Pond' },
              { value: 'creek', label: 'Creek/Stream' },
              { value: 'none', label: 'No Current Source' },
            ]},
            { key: 'primary_source', label: 'Primary Source', type: 'string' },
          ],
          suggestedFlags: [
            { flag: 'big_red', condition: 'No viable water source' },
            { flag: 'green', condition: 'Reliable water source present' },
          ],
        },
        {
          code: 'municipal_water',
          title: 'Municipal water availability',
          description: 'Check public water availability and connection',
          isGroupTask: false,
          dataFields: [
            { key: 'available', label: 'Available', type: 'boolean' },
            { key: 'connected', label: 'Currently Connected', type: 'boolean' },
            { key: 'distance_to_main', label: 'Distance to Main', type: 'number', unit: 'feet' },
            { key: 'connection_cost', label: 'Est. Connection Cost', type: 'number', unit: 'dollars' },
            { key: 'provider', label: 'Water Provider', type: 'string' },
          ],
        },
      ],
    },
    {
      code: 'well_evaluation',
      name: 'Well Evaluation',
      items: [
        {
          code: 'well_inventory',
          title: 'Well inventory',
          description: 'Document all wells on property',
          isGroupTask: false,
          dataFields: [
            { key: 'well_count', label: 'Number of Wells', type: 'number' },
            { key: 'well_types', label: 'Well Types', type: 'array' },
          ],
        },
        {
          code: 'well_records',
          title: 'Well drilling records',
          description: 'Obtain/review well drilling logs',
          isGroupTask: false,
          dataFields: [
            { key: 'records_obtained', label: 'Records Obtained', type: 'boolean' },
            { key: 'drill_date', label: 'Drill Date', type: 'date' },
            { key: 'driller', label: 'Driller', type: 'string' },
            { key: 'total_depth', label: 'Total Depth', type: 'number', unit: 'feet' },
            { key: 'casing_depth', label: 'Casing Depth', type: 'number', unit: 'feet' },
          ],
        },
        {
          code: 'well_yield',
          title: 'Well yield/capacity',
          description: 'Assess well production capacity',
          isGroupTask: false,
          dataFields: [
            { key: 'reported_gpm', label: 'Reported GPM', type: 'number', unit: 'gpm' },
            { key: 'tested_gpm', label: 'Tested GPM', type: 'number', unit: 'gpm' },
            { key: 'recovery_rate', label: 'Recovery Rate', type: 'string' },
            { key: 'adequate_for_needs', label: 'Adequate for Needs', type: 'boolean' },
          ],
          suggestedFlags: [
            { flag: 'big_red', condition: 'Well yield inadequate for intended use' },
            { flag: 'red', condition: 'Marginal well yield' },
            { flag: 'green', condition: 'Good well yield' },
            { flag: 'big_green', condition: 'Excellent well yield' },
          ],
        },
        {
          code: 'well_equipment',
          title: 'Well equipment assessment',
          description: 'Pump, pressure tank, controls condition',
          isGroupTask: false,
          dataFields: [
            { key: 'pump_type', label: 'Pump Type', type: 'string' },
            { key: 'pump_age', label: 'Pump Age', type: 'number', unit: 'years' },
            { key: 'tank_size', label: 'Pressure Tank Size', type: 'number', unit: 'gallons' },
            { key: 'equipment_condition', label: 'Equipment Condition', type: 'select', options: [
              { value: 'excellent', label: 'Excellent' },
              { value: 'good', label: 'Good' },
              { value: 'fair', label: 'Fair' },
              { value: 'poor', label: 'Poor' },
              { value: 'needs_replacement', label: 'Needs Replacement' },
            ]},
            { key: 'replacement_cost', label: 'Est. Replacement Cost', type: 'number', unit: 'dollars' },
          ],
          suggestedFlags: [
            { flag: 'yellow', condition: 'Equipment aging, budget for replacement' },
            { flag: 'red', condition: 'Equipment needs immediate replacement' },
          ],
        },
        {
          code: 'well_location',
          title: 'Well location/setbacks',
          description: 'Verify well meets setback requirements',
          isGroupTask: false,
          dataFields: [
            { key: 'distance_to_septic', label: 'Distance to Septic', type: 'number', unit: 'feet' },
            { key: 'distance_to_property_line', label: 'Distance to Property Line', type: 'number', unit: 'feet' },
            { key: 'meets_setbacks', label: 'Meets Setbacks', type: 'boolean' },
          ],
          suggestedFlags: [
            { flag: 'red', condition: 'Does not meet setback requirements' },
          ],
        },
      ],
    },
    {
      code: 'water_quality',
      name: 'Water Quality',
      items: [
        {
          code: 'water_test',
          title: 'Water quality test',
          description: 'Laboratory water testing results',
          isGroupTask: false,
          dataFields: [
            { key: 'test_date', label: 'Test Date', type: 'date' },
            { key: 'lab_name', label: 'Laboratory', type: 'string' },
            { key: 'test_type', label: 'Test Type', type: 'string' },
            { key: 'passed', label: 'Passed Standards', type: 'boolean' },
          ],
          suggestedFlags: [
            { flag: 'big_red', condition: 'Failed water quality standards' },
            { flag: 'green', condition: 'Passed all tests' },
          ],
        },
        {
          code: 'coliform_test',
          title: 'Coliform/bacteria test',
          description: 'Test for bacterial contamination',
          isGroupTask: false,
          dataFields: [
            { key: 'total_coliform', label: 'Total Coliform', type: 'select', options: [
              { value: 'absent', label: 'Absent' },
              { value: 'present', label: 'Present' },
            ]},
            { key: 'e_coli', label: 'E. Coli', type: 'select', options: [
              { value: 'absent', label: 'Absent' },
              { value: 'present', label: 'Present' },
            ]},
          ],
          suggestedFlags: [
            { flag: 'big_red', condition: 'E. coli present' },
            { flag: 'red', condition: 'Coliform present' },
            { flag: 'green', condition: 'No bacteria detected' },
          ],
        },
        {
          code: 'nitrate_test',
          title: 'Nitrate levels',
          description: 'Test for nitrate contamination',
          isGroupTask: false,
          dataFields: [
            { key: 'nitrate_level', label: 'Nitrate Level', type: 'number', unit: 'mg/L' },
            { key: 'exceeds_mcl', label: 'Exceeds MCL', type: 'boolean' },
          ],
          suggestedFlags: [
            { flag: 'big_red', condition: 'Nitrates exceed safe levels (>10 mg/L)' },
            { flag: 'yellow', condition: 'Elevated nitrates (5-10 mg/L)' },
          ],
        },
        {
          code: 'hardness_minerals',
          title: 'Hardness and minerals',
          description: 'Test for water hardness and mineral content',
          isGroupTask: false,
          dataFields: [
            { key: 'hardness', label: 'Hardness', type: 'number', unit: 'gpg' },
            { key: 'iron', label: 'Iron', type: 'number', unit: 'mg/L' },
            { key: 'manganese', label: 'Manganese', type: 'number', unit: 'mg/L' },
            { key: 'ph', label: 'pH', type: 'number' },
            { key: 'treatment_needed', label: 'Treatment Needed', type: 'boolean' },
          ],
          suggestedFlags: [
            { flag: 'yellow', condition: 'Water treatment recommended' },
          ],
        },
        {
          code: 'special_contaminants',
          title: 'Special contaminants check',
          description: 'Test for area-specific concerns (arsenic, radon, etc.)',
          isGroupTask: false,
          dataFields: [
            { key: 'contaminants_tested', label: 'Contaminants Tested', type: 'array' },
            { key: 'issues_found', label: 'Issues Found', type: 'array' },
            { key: 'remediation_needed', label: 'Remediation Needed', type: 'boolean' },
          ],
          suggestedFlags: [
            { flag: 'big_red', condition: 'Dangerous contaminants present' },
            { flag: 'red', condition: 'Contaminants requiring treatment' },
          ],
        },
      ],
    },
    {
      code: 'water_rights',
      name: 'Water Rights',
      items: [
        {
          code: 'water_rights_type',
          title: 'Water rights assessment',
          description: 'Understand water rights attached to property',
          isGroupTask: false,
          dataFields: [
            { key: 'rights_type', label: 'Rights Type', type: 'select', options: [
              { value: 'riparian', label: 'Riparian' },
              { value: 'appropriative', label: 'Appropriative/Prior Appropriation' },
              { value: 'groundwater', label: 'Groundwater' },
              { value: 'none', label: 'No Specific Rights' },
              { value: 'unknown', label: 'Unknown' },
            ]},
            { key: 'rights_documented', label: 'Rights Documented', type: 'boolean' },
            { key: 'allocation_amount', label: 'Allocation Amount', type: 'string' },
          ],
          suggestedFlags: [
            { flag: 'big_green', condition: 'Strong water rights attached' },
            { flag: 'yellow', condition: 'Water rights unclear' },
            { flag: 'red', condition: 'Limited or contested water rights' },
          ],
        },
        {
          code: 'irrigation_rights',
          title: 'Irrigation rights',
          description: 'Check for irrigation district membership/rights',
          isGroupTask: false,
          dataFields: [
            { key: 'has_irrigation_rights', label: 'Has Irrigation Rights', type: 'boolean' },
            { key: 'district_name', label: 'District Name', type: 'string' },
            { key: 'allocation', label: 'Water Allocation', type: 'string' },
            { key: 'annual_cost', label: 'Annual Cost', type: 'number', unit: 'dollars' },
          ],
        },
        {
          code: 'groundwater_restrictions',
          title: 'Groundwater restrictions',
          description: 'Check for aquifer/groundwater limitations',
          isGroupTask: false,
          dataFields: [
            { key: 'restrictions_exist', label: 'Restrictions Exist', type: 'boolean' },
            { key: 'aquifer_status', label: 'Aquifer Status', type: 'string' },
            { key: 'permit_required', label: 'Permit Required for New Well', type: 'boolean' },
          ],
          suggestedFlags: [
            { flag: 'red', condition: 'Severe groundwater restrictions' },
            { flag: 'yellow', condition: 'Some groundwater limitations' },
          ],
        },
      ],
    },
    {
      code: 'new_well',
      name: 'New Well Potential',
      items: [
        {
          code: 'new_well_feasibility',
          title: 'New well feasibility',
          description: 'Assess viability of drilling new well',
          isGroupTask: false,
          dataFields: [
            { key: 'feasible', label: 'Feasible', type: 'boolean' },
            { key: 'permit_required', label: 'Permit Required', type: 'boolean' },
            { key: 'estimated_depth', label: 'Estimated Depth', type: 'number', unit: 'feet' },
            { key: 'estimated_cost', label: 'Estimated Cost', type: 'number', unit: 'dollars' },
            { key: 'area_water_table', label: 'Area Water Table Info', type: 'string' },
          ],
        },
        {
          code: 'neighbor_wells',
          title: 'Neighbor well research',
          description: 'Research neighboring property well information',
          isGroupTask: false,
          dataFields: [
            { key: 'wells_researched', label: 'Wells Researched', type: 'number' },
            { key: 'average_depth', label: 'Average Depth', type: 'number', unit: 'feet' },
            { key: 'average_yield', label: 'Average Yield', type: 'number', unit: 'gpm' },
            { key: 'known_issues', label: 'Known Issues', type: 'array' },
          ],
        },
      ],
    },
  ],
};

